<script>
export default {
  props: {
    propertyRow: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      propertyDetails: null,
      loading: false,
    };
  },
  methods: {
    getDetails(row) {
      row.itemVisible = !row.itemVisible;
      if (row.itemVisible) {
        this.loading = true;
        setTimeout(() => {
          this.$store
            .dispatch("property/GetSingleProperty", row.propertyId ? row.propertyId : row.id)
            .then((res) => {
              if(res.data.error){
                this.showToast(res.data.errorMessage, "error");
                this.loading = false;
                return;
              }
              this.loading = false;
              this.propertyDetails = res.data;
            })
            .catch(() => {
              this.loading = false;
              this.showToast(
                "Something went wrong - Getting assignments!",
                "error"
              );
            });
        }, 1000);
      }
    },
  },
};
</script>

<template>
  <div>
    <b-card-body class="border cursor-pointer" @click="getDetails(propertyRow)">
      <div class="d-flex align-items-center justify-content-between">
        <h6 class="mb-0">
          <i class="bx bx-menu-alt-right"></i>
          Property Details
        </h6>
        <h5 class="mb-0">
          <i class="bx bx-chevron-right rotate" :class="{
            'rotate-90': propertyRow.itemVisible,
          }"></i>
        </h5>
      </div>
    </b-card-body>

    <b-collapse :visible="propertyRow.itemVisible" role="tabpanel">
      <div v-if="propertyRow.itemVisible">
        <b-card-body v-if="loading" class="border">
          <div class="d-flex align-items-center justify-content-center">
            <div class="preloader-component me-2">
              <div class="status">
                <div class="spinner-chase w-25px h-25px">
                  <div class="chase-dot"></div>
                  <div class="chase-dot"></div>
                  <div class="chase-dot"></div>
                  <div class="chase-dot"></div>
                  <div class="chase-dot"></div>
                  <div class="chase-dot"></div>
                </div>
              </div>
            </div>
            <h5 class="mb-0">Loading...</h5>
          </div>
        </b-card-body>

        <b-card-body v-else class="border">
          <b-row class="gx-5">
            <b-col lg="8" sm="12">
              <div class="mb-4">
                <h5 class="border-bottom pb-2 mb-3">Property Information</h5>
                <div class="border rounded-3 p-3">
                  <b-row class="mb-3">
                    <b-col md="4" sm="12" class="fw-bold">Property Name:</b-col>
                    <b-col md="8" sm="12"
                      :class="{ 'text-white': propertyDetails.name && propertyDetails.name != 'Not Indicated' }">
                      {{ propertyDetails.name ? propertyDetails.name : "Not Indicated" }}
                    </b-col>
                  </b-row>
                  <b-row class="mb-3">
                    <b-col md="4" sm="12" class="fw-bold">PO Number:</b-col>
                    <b-col md="8" sm="12"
                      :class="{ 'text-white': propertyDetails.poNumber && propertyDetails.poNumber != 'Not Indicated' }">
                      {{ propertyDetails.poNumber ? propertyDetails.poNumber : "Not Indicated" }}
                    </b-col>
                  </b-row>
                  <b-row class="mb-3">
                    <b-col md="4" sm="12" class="fw-bold">Property Number:</b-col>
                    <b-col md="8" sm="12"
                      :class="{ 'text-white': propertyDetails.propertyNumber && propertyDetails.propertyNumber != 'Not Indicated' }">
                      {{ propertyDetails.propertyNumber ? propertyDetails.propertyNumber : "Not Indicated" }}
                    </b-col>
                  </b-row>
                  <b-row class="mb-3">
                    <b-col md="4" sm="12" class="fw-bold">{{ propertyDetails.amount >= 50000 ? "PAR" : "ICS" }}
                      Number:</b-col>
                    <b-col md="8" sm="12"
                      :class="{ 'text-white': propertyDetails.currentAssignment && propertyDetails.currentAssignment.assignmentNumber }">
                      {{ propertyDetails.currentAssignment && propertyDetails.currentAssignment.assignmentNumber ?
                        propertyDetails.currentAssignment.assignmentNumber : "Not Indicated" }}
                    </b-col>
                  </b-row>
                  <b-row class="mb-3">
                    <b-col md="4" sm="12" class="fw-bold">Serial Number:</b-col>
                    <b-col md="8" sm="12"
                      :class="{ 'text-white': propertyDetails.serialNumber && propertyDetails.serialNumber != 'Not Indicated' }">
                      {{ propertyDetails.serialNumber ? propertyDetails.serialNumber : "Not Indicated" }}
                    </b-col>
                  </b-row>
                  <b-row class="mb-3">
                    <b-col md="4" sm="12" class="fw-bold">Date Acquired:</b-col>
                    <b-col md="8" sm="12"
                      :class="{ 'text-white': propertyDetails.dateAcquired && !propertyDetails.dateAcquired.includes('0001-01-01') }">
                      {{ propertyDetails.dateAcquired && !propertyDetails.dateAcquired.includes("0001-01-01") ?
                        formatDate(new Date(propertyDetails.dateAcquired)) : "Not Indicated" }}
                    </b-col>
                  </b-row>
                  <b-row class="mb-3">
                    <b-col md="4" sm="12" class="fw-bold">Warranty Date:</b-col>
                    <b-col md="8" sm="12"
                      :class="{ 'text-white': propertyDetails.warrantyStartDate && !propertyDetails.warrantyStartDate.includes('0001-01-01') }">
                      {{ propertyDetails.warrantyStartDate && !propertyDetails.warrantyStartDate.includes("0001-01-01")
                        ? `${formatDate(new Date(propertyDetails.warrantyStartDate))} - ${formatDate(new
                          Date(propertyDetails.warrantyEndDate))}` : "Not Indicated" }}
                    </b-col>
                  </b-row>
                  <b-row class="mb-3">
                    <b-col md="4" sm="12" class="fw-bold">Item Category:</b-col>
                    <b-col md="8" sm="12" :class="{ 'text-white': propertyDetails.itemCategory }">
                      {{ propertyDetails.itemCategory ? propertyDetails.itemCategory.name : "Not Indicated" }}
                    </b-col>
                  </b-row>
                  <b-row class="mb-3">
                    <b-col md="4" sm="12" class="fw-bold">Supplier:</b-col>
                    <b-col md="8" sm="12" :class="{ 'text-white': propertyDetails.supplier }">
                      {{ propertyDetails.supplier ? propertyDetails.supplier : "Not Indicated" }}
                    </b-col>
                  </b-row>
                  <b-row class="mb-3">
                    <b-col md="4" sm="12" class="fw-bold">Fund Source:</b-col>
                    <b-col md="8" sm="12" :class="{ 'text-white': propertyDetails.fundCluster }">
                      {{ propertyDetails.fundCluster ? propertyDetails.fundCluster : "Not Indicated" }}
                    </b-col>
                  </b-row>
                  <b-row class="mb-3">
                    <b-col md="4" sm="12" class="fw-bold">Fund Cluster:</b-col>
                    <b-col md="8" sm="12" :class="{ 'text-white': propertyDetails.cluster }">
                      {{ propertyDetails.cluster ? propertyDetails.cluster.cluster : "Not Indicated" }}
                    </b-col>
                  </b-row>
                  <b-row class="mb-3">
                    <b-col md="4" sm="12" class="fw-bold">Property Condition:</b-col>
                    <b-col md="8" sm="12" :class="{ 'text-white': propertyDetails.condition }">
                      {{ propertyDetails.condition ? propertyDetails.condition : "Not Indicated" }}
                    </b-col>
                  </b-row>
                  <b-row class="mb-3">
                    <b-col md="4" sm="12" class="fw-bold">Unit:</b-col>
                    <b-col md="8" sm="12" :class="{ 'text-white': propertyDetails.unit }">
                      {{ propertyDetails.unit ? propertyDetails.unit : "Not Indicated" }}
                    </b-col>
                  </b-row>
                </div>
              </div>

              <div class="mb-4">
                <h5 class="border-bottom pb-2 mb-3">Specifications</h5>
                <div class="border rounded-3 p-3">
                  <b-row class="mb-3">
                    <b-col md="4" sm="12" class="fw-bold">Property Specification:</b-col>
                    <b-col md="8" sm="12" :class="{ 'text-white': propertyDetails.specification }">
                      {{ propertyDetails.specification ? propertyDetails.specification : "Not Indicated" }}
                    </b-col>
                  </b-row>
                  <b-row class="mb-3">
                    <b-col md="4" sm="12" class="fw-bold">Brand:</b-col>
                    <b-col md="8" sm="12" :class="{ 'text-white': propertyDetails.brand }">
                      {{ propertyDetails.brand ? propertyDetails.brand : "Not Indicated" }}
                    </b-col>
                  </b-row>
                  <b-row class="mb-3">
                    <b-col md="4" sm="12" class="fw-bold">Model:</b-col>
                    <b-col md="8" sm="12" :class="{ 'text-white': propertyDetails.model }">
                      {{ propertyDetails.model ? propertyDetails.model : "Not Indicated" }}
                    </b-col>
                  </b-row>
                  <b-row class="mb-3">
                    <b-col md="4" sm="12" class="fw-bold">Color:</b-col>
                    <b-col md="8" sm="12" :class="{ 'text-white': propertyDetails.color }">
                      {{ propertyDetails.color ? propertyDetails.color : "Not Indicated" }}
                    </b-col>
                  </b-row>
                  <b-row class="mb-3">
                    <b-col md="4" sm="12" class="fw-bold">Dimension Description:</b-col>
                    <b-col md="8" sm="12" :class="{ 'text-white': propertyDetails.dimensionDescription }">
                      {{ propertyDetails.dimensionDescription ? propertyDetails.dimensionDescription : "Not Indicated"
                      }}
                    </b-col>
                  </b-row>
                  <b-row class="mb-3">
                    <b-col md="4" sm="12" class="fw-bold">Material Used:</b-col>
                    <b-col md="8" sm="12" :class="{ 'text-white': propertyDetails.materialUsed }">
                      {{ propertyDetails.materialUsed ? propertyDetails.materialUsed : "Not Indicated" }}
                    </b-col>
                  </b-row>
                  <b-row class="mb-3">
                    <b-col md="4" sm="12" class="fw-bold">Package Description:</b-col>
                    <b-col md="8" sm="12" :class="{ 'text-white': propertyDetails.packageDescription }">
                      {{ propertyDetails.packageDescription ? propertyDetails.packageDescription : "Not Indicated" }}
                    </b-col>
                  </b-row>
                  <b-row v-if="propertyDetails.note" class="mb-3">
                    <b-col md="4" sm="12" class="fw-bold">Notes:</b-col>
                    <b-col md="8" sm="12" class="text-white">{{ propertyDetails.note }}</b-col>
                  </b-row>
                </div>
              </div>
            </b-col>

            <b-col lg="4" sm="12">
              <div class="mb-4">
                <h5 class="border-bottom pb-2 mb-3">Value Information</h5>
                <div class="border rounded-3 p-3">
                  <b-row class="mb-3">
                    <b-col sm="5" class="fw-bold">Amount:</b-col>
                    <b-col sm="7" :class="{ 'text-white': propertyDetails.amount }">
                      {{ propertyDetails.amount ? formatCurrency(propertyDetails.amount) : "Not Indicated" }}
                    </b-col>
                  </b-row>
                  <b-row class="mb-3">
                    <b-col sm="5" class="fw-bold">Life Span in Years:</b-col>
                    <b-col sm="7" :class="{ 'text-white': propertyDetails.lifespanInYears }">
                      {{ propertyDetails.lifespanInYears > 0 ? propertyDetails.lifespanInYears : "Not Indicated" }}
                    </b-col>
                  </b-row>
                  <b-row class="mb-3">
                    <b-col sm="5" class="fw-bold">Scrap Value:</b-col>
                    <b-col sm="7" :class="{ 'text-info': propertyDetails.salvageValue }">
                      {{ propertyDetails.salvageValue }}%
                    </b-col>
                  </b-row>
                  <b-row class="mb-3">
                    <b-col sm="5" class="fw-bold">Yearly Depreciation:</b-col>
                    <b-col sm="7" :class="{ 'text-danger': propertyDetails.yearlyDepreciation }">
                      {{ formatCurrency(propertyDetails.yearlyDepreciation) }}
                    </b-col>
                  </b-row>
                  <b-row class="mb-3">
                    <b-col sm="5" class="fw-bold">Current Value:</b-col>
                    <b-col sm="7" :class="{ 'text-warning': propertyDetails.currentValue }">
                      {{ formatCurrency(propertyDetails.currentValue) }}
                    </b-col>
                  </b-row>
                </div>
              </div>

              <div v-if="propertyDetails.note" class="mb-4">
                <h5 class="border-bottom pb-2 mb-3">Notes</h5>
                <div class="border rounded-3 p-3">
                  <p class="text-white mb-0">{{ propertyDetails.note }}</p>
                </div>
              </div>
            </b-col>
          </b-row>
        </b-card-body>
      </div>
    </b-collapse>
  </div>
</template>
